/**
 * Export stĺpov z mapy do súboru.
 *
 * Beží celý v prehliadači — dáta sú už v store, takže netreba volať server.
 * CSV je zámerne v tvare, ktorý appka vie znova naimportovať (súradnice ako
 * prvé dva stĺpce, bodkočiarka ako oddeľovač, BOM kvôli diakritike v Exceli).
 */
import React, { useState } from 'react';
import { Pole } from '../../types';
import { ExportFormat, exportujStlpy, vyberStlpov, nazovSuboru } from '../../utils/poleExport';
import './Dialog.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  poles: Pole[];
  nazovProjektu: string;
}

export const ExportPolesDialog: React.FC<Props> = ({ isOpen, onClose, poles, nazovProjektu }) => {
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [bezVirtualnych, setBezVirtualnych] = useState(true);
  const [hotovo, setHotovo] = useState<string | null>(null);

  if (!isOpen) return null;

  const vybrane = vyberStlpov(poles, { bezVirtualnych });
  const virtualnych = poles.filter((p) => p.is_virtual).length;
  const bezSuradnic = poles.filter(
    (p) => (bezVirtualnych ? !p.is_virtual : true)
      && (p.latitude === undefined || p.latitude === null || p.latitude === ''
       || p.longitude === undefined || p.longitude === null || p.longitude === '')).length;

  const exportuj = () => {
    const n = exportujStlpy(poles, nazovProjektu, format, { bezVirtualnych });
    setHotovo(n > 0 ? `Stiahnutých ${n} stĺpov do ${nazovSuboru(nazovProjektu, format)}` : null);
  };

  const zavri = () => {
    setHotovo(null);
    onClose();
  };

  return (
    <div className="dialog-overlay" onClick={zavri}>
      <div className="dialog" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>📤 Export stĺpov</h2>
          <button className="dialog-close" onClick={zavri}>✕</button>
        </div>

        <div className="dialog-content">
          <div className="form-group">
            <label>Formát</label>
            <label className="radio-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', marginBottom: '8px' }}>
              <input type="radio" checked={format === 'csv'} onChange={() => setFormat('csv')} />
              <span>
                <b>CSV</b> — otvorí sa v Exceli a dá sa ním stĺpy znova naimportovať
                späť do appky.
              </span>
            </label>
            <label className="radio-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
              <input type="radio" checked={format === 'kml'} onChange={() => setFormat('kml')} />
              <span>
                <b>KML</b> — otvorí sa v Google Earth alebo MyMaps.
              </span>
            </label>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={bezVirtualnych}
                onChange={(e) => setBezVirtualnych(e.target.checked)}
              />
              <span>Vynechať zákaznícke kazety ({virtualnych})</span>
            </label>
            <small className="form-hint">
              Zákaznícke kazety sú virtuálne stĺpy na tých istých súradniciach ako ich
              rodič — v exporte by boli len dvojice bodov na jednom mieste.
            </small>
          </div>

          <div className="form-group">
            <label>Na export</label>
            <div style={{ fontSize: '20px', fontWeight: 700 }}>{vybrane.length} stĺpov</div>
            {bezSuradnic > 0 && (
              <small className="form-hint" style={{ color: '#e0a800' }}>
                ⚠️ {bezSuradnic} stĺpov nemá súradnice, tie sa do súboru nedostanú.
              </small>
            )}
          </div>

          {hotovo && (
            <div style={{
              background: '#14532d', color: '#86efac', borderRadius: '6px',
              padding: '10px 14px', fontSize: '14px',
            }}>
              ✅ {hotovo}
            </div>
          )}
        </div>

        <div className="dialog-actions">
          <button className="btn-secondary" onClick={zavri}>Zavrieť</button>
          <button className="btn-primary" onClick={exportuj} disabled={vybrane.length === 0}>
            📤 Stiahnuť {format.toUpperCase()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportPolesDialog;
